import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, mergeClasses } from '@fluentui/react-components';
import { Dismiss20Regular, ReOrderDotsVertical20Regular, Settings20Regular } from '@fluentui/react-icons';

export interface WidgetFrameProps {
  title: string;
  icon?: React.ReactNode;
  /** Modo "Personalizar": muestra el agarre, el engranaje y el botón de quitar. */
  editing: boolean;
  hasSettings?: boolean;
  onSettings?: () => void;
  onRemove?: () => void;
  /** Vínculo "Ver todo" al pie. */
  link?: { to: string; label: string };
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Marco de un widget del panel: encabezado (ícono, título y acciones de edición), cuerpo con scroll
 * propio y pie opcional. En modo edición el encabezado es el agarre para arrastrar (`.dbw__head`).
 */
export const WidgetFrame = ({ title, icon, editing, hasSettings, onSettings, onRemove, link, children, className, testId }: WidgetFrameProps) => {
  const { t } = useTranslation();
  return (
    <section className={mergeClasses('dbw', editing && 'dbw--editing', className)} aria-label={title} data-testid={testId}>
      <header className="dbw__head">
        {editing && (
          <span className="dbw__grip" aria-hidden="true" title={t('dashboard.frame.drag', { title })}>
            <ReOrderDotsVertical20Regular />
          </span>
        )}
        {icon && (
          <span className="dbw__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <h3 className="dbw__title">{title}</h3>
        {editing && (
          <div className="dbw__actions">
            {hasSettings && (
              <Button
                appearance="subtle"
                size="small"
                icon={<Settings20Regular />}
                aria-label={t('dashboard.frame.settings', { title })}
                title={t('dashboard.frame.settings', { title })}
                onClick={onSettings}
                data-testid="widget-settings"
              />
            )}
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss20Regular />}
              aria-label={t('dashboard.frame.remove', { title })}
              title={t('dashboard.frame.remove', { title })}
              onClick={onRemove}
              data-testid="widget-remove"
            />
          </div>
        )}
      </header>
      <div className="dbw__body">{children}</div>
      {link && !editing && (
        <footer className="dbw__foot">
          <Link to={link.to} className="dbw__link">
            {link.label}
          </Link>
        </footer>
      )}
    </section>
  );
};

export default WidgetFrame;
